import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Loader2 } from "lucide-react";
import api, { cachedGet } from "@/lib/api";
import { euro, formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CATEGORIE } from "@/components/QuoteBuilder";

const UNITA = ["mq", "ml", "cad", "a corpo", "mc", "kg", "ore", "gg"];
const EMPTY = { codice: "", nome: "", categoria: "Opere Edili e Isolamenti", prezzo: 0, iva: 22, unita: "mq" };

export default function Listino() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("tutte");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => cachedGet("/prices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const fetchCode = async (categoria) => {
    try {
      const r = await api.get(`/prices/next-code?categoria=${encodeURIComponent(categoria || "")}`);
      setForm((f) => ({ ...f, codice: r.data.codice }));
    } catch (_) {}
  };
  const setCategoria = (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, categoria: val }));
    if (!editId) fetchCode(val);
  };
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); fetchCode(EMPTY.categoria); };
  const openEdit = (p) => { setForm(p); setEditId(p.id); setOpen(true); };

  const save = async () => {
    if (!form.nome) { toast.error("Inserisci il nome della voce"); return; }
    setSaving(true);
    try {
      const payload = { ...form, prezzo: Number(form.prezzo) || 0, iva: Number(form.iva) || 0 };
      if (editId) await api.put(`/prices/${editId}`, payload);
      else await api.post("/prices", payload);
      toast.success(editId ? "Voce aggiornata" : "Voce aggiunta");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    await api.delete(`/prices/${toDelete.id}`);
    toast.success("Voce eliminata");
    setToDelete(null); load();
  };

  const cats = ["tutte", ...Array.from(new Set(items.map((i) => i.categoria)))];
  const filtered = items.filter((p) => {
    const mq = !q || [p.codice, p.nome].join(" ").toLowerCase().includes(q.toLowerCase());
    const mc = cat === "tutte" || p.categoria === cat;
    return mq && mc;
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0 flex-wrap">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Cerca voce o codice…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="price-search-input" />
          </div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c === "tutte" ? "Tutte le categorie" : c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={openNew} data-testid="price-item-add-button"><Plus className="w-4 h-4 mr-1.5" /> Nuova Voce</Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <Table data-testid="price-list-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Cod.</TableHead>
              <TableHead>Descrizione</TableHead>
              <TableHead className="hidden md:table-cell">Categoria</TableHead>
              <TableHead className="hidden sm:table-cell">U.M.</TableHead>
              <TableHead className="text-right">Prezzo</TableHead>
              <TableHead className="hidden sm:table-cell text-right">IVA</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.id} data-testid={`price-row-${p.id}`}>
                <TableCell className="font-mono text-xs">{p.codice || "—"}</TableCell>
                <TableCell className="font-medium">{p.nome}</TableCell>
                <TableCell className="hidden md:table-cell"><Badge variant="secondary" className="font-normal">{p.categoria}</Badge></TableCell>
                <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{p.unita}</TableCell>
                <TableCell className="text-right font-semibold">{euro(p.prezzo)}</TableCell>
                <TableCell className="hidden sm:table-cell text-right text-sm text-muted-foreground">{p.iva}%</TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`price-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setToDelete(p)} data-testid={`price-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">Nessuna voce trovata.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="price-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Modifica voce" : "Nuova voce di listino"}</DialogTitle><DialogDescription className="sr-only">Inserisci o modifica una voce del listino prezzi.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Codice</Label><Input value={form.codice} onChange={set("codice")} placeholder="Automatico (es. A.01) — modificabile" data-testid="price-code-input" /></div>
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Input list="price-cat-list" value={form.categoria} onChange={setCategoria} />
              <datalist id="price-cat-list">{CATEGORIE.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div className="space-y-2 sm:col-span-2"><Label>Descrizione *</Label><Input value={form.nome} onChange={set("nome")} data-testid="price-name-input" /></div>
            <div className="space-y-2">
              <Label>Unità di misura</Label>
              <Select value={form.unita} onValueChange={(u) => setForm((f) => ({ ...f, unita: u }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{UNITA.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Prezzo unitario €</Label><Input type="number" min="0" step="0.01" value={form.prezzo} onChange={set("prezzo")} data-testid="price-value-input" /></div>
            <div className="space-y-2"><Label>Aliquota IVA %</Label><Input type="number" min="0" value={form.iva} onChange={set("iva")} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="price-save-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la voce?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.nome} sarà rimossa dal listino.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
